<template>
  <ResponseTile
    v-if="showErrorTile"
    :back-button-label="$t('requestIVKList.response.buttonLabel')"
    :text="$t('requestIVKList.response.serviceErrorText')"
    :title="$t('requestIVKList.response.serviceErrorTitle')"
    @button:back="formAbortActionToServicePage"
  ></ResponseTile>
  <ResponseTile
    v-else-if="!showErrorTile && showNoContractsTile"
    :back-button-label="errorButtonLabel"
    :text="errorText"
    :title="errorTitle"
    @button:back="formAbortActionToServicePage()"
  ></ResponseTile>
  <div v-else>
    <Spinner v-if="formLoading" class="fix-spinner"></Spinner>
    <div v-else>
      <SelfServiceTile
        :apiRequestConfig="apiRequestConfig"
        :changeRequestType="changeRequestType()"
        :checkPoliciesDetails="checkPoliciesDetails"
        :checkPolicyDetails="checkPolicyDetails"
        :checkPolicyOverview="checkPolicyOverview"
        :formAbortAction="formAbortActionToServicePage"
        :formLoading="formLoading"
        :formShowResponse="formShowResponse"
        :formSubmitDisabled="formSubmitDisabled"
        :formSubmitting="formSubmitting"
        :getFormBlocks="getFormBlocks"
        :getListBlocks="getListBlocks"
        :modifyPolicy="modifyPolicy"
        :multiSelect="false"
        :preSelectedPolicyIds="preSelectedPolicyIds"
        :selfServiceLabels="selfServiceLabels"
        :setSelectedPolicies="setSelectedPolicies"
        :showAttentionAlert="false"
        :showDateOption="false"
        :showInfobox="false"
        :staticPoliciesWithIconAndTitles="staticPoliciesFiltered"
        @formSubmit="formSubmit"
      >
        <template v-slot:change-component>
          <div class="tc-data-row flex-column">
            <Checkbox
              v-model="useCurrentMail"
              isRadio
              @click="setFormSubmitDisabled(false)"
            >
              <template v-slot:label>{{ currentMailText }}</template>
            </Checkbox>
            <Checkbox v-model="useNewMail" isRadio @click="setFormSubmitDisabled(false)">
              <template v-slot:label>{{ newMailText }}</template>
            </Checkbox>
          </div>
        </template>
      </SelfServiceTile>
      <br />
      <template v-if="!isDa()">
        <p class="self-service-selection-headline">
          {{ $t('requestIVKList.footer.title') }}
        </p>
        {{ $t('requestIVKList.footer.ivkRequestText') }}
        <a :href="onlineFormularLink" target="_blank">{{
          $t('requestIVKList.footer.onlineFormularLink')
        }}</a
        >.
        <br />
        <br />
      </template>
      {{ $t('requestIVKList.footer.ratgeberPrefix') }}
      <a :href="ratgeberLink" target="_blank">{{
        $t('requestIVKList.footer.ratgeberLink')
      }}</a>
      {{ $t('requestIVKList.footer.ratgeberSuffix') }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, type Ref, ref } from 'vue'
import { type Policy } from 'policies-api'
import {
  Checkbox,
  type KeyValueType,
  type PolicyWithIconAndTitles,
  ResponseTile,
  type SelfServiceLabels,
  SelfServiceTile,
  Spinner
} from 'de-zurich-ui-library'
import { useI18n } from 'vue-i18n'
import { navigate } from '@/utils/NavigationUtils'
import { requestIvkCertificate } from '@/stores/policiesService'
import { PolicyList } from '@/utils/policy/PolicyList'
import { selfServiceListMotorOnlyOnMounted } from '@/utils/policy/PolicyUtils'
import { usePoliciesStore } from '@/stores/policiesStore'
import { useSelfServicePolicyList } from '@/composables/useSelfServicePolicyList'
import { getEmail } from '@/utils/KeycloakUtils'
import { isDa } from '@/utils/CommonUtils'
import { useMotorStore } from '@/stores/motorStore'
import type { MotorContract } from '@/types/motor/MotorContract'
import { SelfServiceProcess } from '@/types/SelfServiceProcess'

const policiesStore = usePoliciesStore()

const { t } = useI18n()

const {
  staticPoliciesFiltered,
  formLoading,
  showErrorTile,
  selectedPolicies,
  loadPolicies,
  setFormLoading,
  setShowErrorTile,
  setSelectedPolicies,
  formAbortActionToServicePage,
  modifyPolicy,
  checkPolicyOverview,
  checkPolicyDetails,
  checkPoliciesDetails,
  apiRequestConfig
} = useSelfServicePolicyList(selfServiceListMotorOnlyOnMounted)

// authorization and redirects
const formSubmitting = ref(false)
const formSubmitDisabled = ref(true)
const formShowResponse = ref(false)
const formStartDate = ref('')
const showNoContractsTile = ref(false)
const selectedMail = ref<boolean | null>(null)
const selfServiceLabels = computed<SelfServiceLabels>(() => ({
  listHeadline: t('requestIVKList.list.listHeadline').toString(),
  errorTitle: t('requestIVKList.error.errorTitle').toString(),
  errorText: t('requestIVKList.error.errorText').toString(),
  formSubmitButtonLabel: t(
    !formSubmitDisabled.value && selectedMail.value === false
      ? 'requestIVKList.form.formChangeEmailButtonLabel'
      : 'requestIVKList.form.formSubmitButtonLabel'
  ).toString(),
  formCardTitle: t('requestIVKList.form.formCardTitle').toString(),
  formCurrentDataLabel: t('requestIVKList.form.formCurrentBankData').toString(),
  formCurrentDataValue: '',
  formDateLabel: t('requestIVKList.form.formInputLabelDate').toString(),
  formDateErrorText: t('requestIVKList.form.formDateErrorText').toString(),
  responseTitle: t('requestIVKList.response.thankYouTitle').toString(),
  responseText: t('requestIVKList.response.thankYouText').toString(),
  responseTileBackButtonLabel: t('requestIVKList.response.buttonLabel').toString()
}))
const preSelectedPolicyIds: Ref<string[]> = ref([])

const ratgeberLink: Ref<string> = ref(
  globalThis.ENV_CONFIG?.REDIRECTS?.IVK_RATGEBER ?? ''
)

const onlineFormularLink: Ref<string> = ref(
  globalThis.ENV_CONFIG?.REDIRECTS?.IVK_REQUEST ?? ''
)

const errorTitle = ref('')
const errorText = ref('')
const errorButtonLabel = ref('')

const useCurrentMail = computed<boolean>({
  get: () => selectedMail.value === true,
  set: (value) => {
    selectedMail.value = value ? true : null
  }
})
const useNewMail = computed<boolean>({
  get: () => selectedMail.value === false,
  set: (value) => {
    selectedMail.value = value ? false : null
  }
})
const currentMailText = ref(getEmail() ?? '')
const newMailText = ref(
  'Falls Sie die E-Mail Adresse ändern möchten, wählen sie diese Option.'
)

enum ChangeRequestType {
  IVKAnfordern = 'IVKAnfordern'
}

onMounted(async () => {
  // Preselection: consume all policy ids once for the self-service list
  preSelectedPolicyIds.value = policiesStore.consumePreSelectedPolicyIds()

  try {
    const filtered = await loadPolicies()
    if (filtered.length === 0) {
      setErrorTileNoContracts()
    }
  } catch {
    setErrorTileServiceDown()
  } finally {
    setFormLoading(false)
  }
})

function setErrorTileNoContracts() {
  errorTitle.value = t('requestIVKList.error.errorTitle')
  errorText.value = t('requestIVKList.error.errorText')
  errorButtonLabel.value = t('requestIVKList.error.buttonLabel')
  setShowNoContractsTile(true)
}

function changeRequestType(): ChangeRequestType {
  return ChangeRequestType.IVKAnfordern
}

// Default Methods

function setFormSubmitDisabled(value: boolean): void {
  formSubmitDisabled.value = value
}

function setShowNoContractsTile(value: boolean): void {
  showNoContractsTile.value = value
}

function setFormInputStartDate(value: string): void {
  formStartDate.value = value
}

function setErrorTileServiceDown() {
  setShowErrorTile(true)
}

async function formSubmit(): Promise<void> {
  if (selectedMail.value === false) {
    policiesStore.setProcessReturnContext(
      SelfServiceProcess.REQUEST_IVK_PAGE,
      selectedPolicies.value.map((policy) => policy.policyId)
    )
    navigate(globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_EMAIL_PAGE)
    return
  }

  formSubmitting.value = true
  const policyId = selectedPolicies.value[0]?.policyId ?? ''

  try {
    const motorContract = (await useMotorStore().getMotorContractByPolicyId(
      policyId
    )) as MotorContract
    const isOldtimer = motorContract?.insuredVehicle?.fleetNumber === '00009003'
    const licensePlate =
      (selectedPolicies.value as unknown as PolicyWithIconAndTitles[])[0]?.secondTitle ??
      ''
    const normalizedLicensePlate = licensePlate.replaceAll(' ', '-')
    const mail = getEmail() ?? ''

    const response = await requestIvkCertificate(policyId)
    if (response.ok) {
      policiesStore.setIvkThankYouValues({
        mail,
        licensePlate: normalizedLicensePlate,
        isOldtimer
      })
      navigate(`/danke?internationale-versicherungskarte-anfordern`)
    } else {
      setErrorTileServiceDown()
      formLoading.value = false
    }
  } catch {
    setErrorTileServiceDown()
    formLoading.value = false
  } finally {
    formSubmitting.value = false
  }
}

function getListBlocks(): KeyValueType[] {
  return []
}

function getFormBlocks(policies: Policy[]): KeyValueType[][] {
  return PolicyList.getFormBlocks(policies, 'requestIVKList')
}
</script>

<style lang="scss" scoped>
.fix-spinner {
  position: relative !important;
  top: 64px !important;
}

.is-styled {
  .self-service-selection-headline {
    font-family: var(--sst-headline-font-family), serif;
    font-size: 20px;
    color: var(--sst-headline-color) !important;
    margin-top: 0;
    margin-bottom: calc(var(--baseline) * 2) !important;
  }
}
</style>
